import { forwardRef, type ButtonHTMLAttributes, type ComponentRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '~lib/utils/style';

/**
 * Pill-shaped button. Variants and sizes per DESIGN.md §6 — `primary` and
 * `secondary` cover 95% of cases.
 */
const buttonVariants = cva(
  cn(
    'relative inline-flex max-w-full shrink-0 items-center justify-center gap-x-2',
    'whitespace-nowrap rounded-full font-sans font-semibold tracking-wide',
    'transition-colors motion-reduce:transition-none',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-60',
  ),
  {
    variants: {
      variant: {
        primary:
          'bg-gradient-to-b from-primary to-primary-dark text-primary-foreground hover:from-primary-light hover:to-primary-dark',
        secondary:
          'border border-primary bg-transparent text-accent-container-foreground hover:border-primary-light hover:text-primary',
        tertiary:
          'border border-border bg-transparent text-foreground hover:text-primary',
        ghost: 'bg-transparent text-primary-dark hover:text-primary',
        subtle:
          'bg-card text-primary-dark shadow-sm hover:text-primary',
        danger:
          'bg-gradient-to-b from-destructive to-destructive-dark text-destructive-foreground hover:from-destructive-light hover:to-destructive-dark',
      },
      size: {
        lg: 'h-12 px-5 py-3 text-sm leading-5',
        md: 'h-10 px-4 py-2 text-sm leading-5',
        sm: 'h-8 px-3 py-1 text-xs leading-4',
        'icon-lg': 'aspect-square size-12 p-3',
        'icon-md': 'aspect-square size-10 p-2',
        'icon-sm': 'aspect-square size-8 p-1',
        'icon-xs': 'aspect-square size-6 p-0.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
    },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = forwardRef<ComponentRef<'button'>, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export { Button, buttonVariants };
export type { ButtonProps };
